import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Select, Space, Table, Tabs, Switch, message } from 'antd';
import { adminAPI } from '../api/admin';

const CONFIGS = {
  years: { title: 'سال‌ها', api: 'Years', fields: ['year', 'title', 'farsiTitle', 'description', 'farsiDescription', 'isActive'] },
  missions: { title: 'مأموریت‌ها', api: 'Missions', fields: ['key', 'year', 'title', 'farsiTitle', 'eventName', 'requiredSkillKeys', 'rewardKnuts', 'isFinal', 'isActive', 'order'] },
  skills: { title: 'درخت مهارت', api: 'Skills', fields: ['key', 'name', 'farsiName', 'year', 'branch', 'parentKeys', 'costKnuts', 'isActive', 'order'] },
  tasks: { title: 'تسک‌های جایزه', api: 'Tasks', fields: ['key', 'title', 'farsiTitle', 'year', 'rewardKnuts', 'maxCompletions', 'requiresAdminApproval', 'isActive', 'order'] },
  miniGames: { title: 'مینی‌گیم‌ها', api: 'MiniGames', fields: ['key', 'title', 'farsiTitle', 'minPlayers', 'maxPlayers', 'entryFeeKnuts', 'rewardKnuts', 'requiresAdminApproval', 'isActive', 'order'] }
};

export default function YearGameManager({ role = 'admin' }) {
  const isTeacher = role === 'teacher';
  const [active, setActive] = useState('years');
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const cfg = CONFIGS[active];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI[`getYearGame${cfg.api}`]();
      if (res.data.success) setRows(res.data[active] || res.data.miniGames || []);
    } catch (error) {
      message.error('خطا در بارگذاری داده‌ها');
    } finally {
      setLoading(false);
    }
  }, [active, cfg]);

  useEffect(() => {
    if (!isTeacher) load();
  }, [isTeacher, load]);

  const submit = async (values) => {
    const payload = normalize(values);
    try {
      if (editing?._id) await adminAPI[`updateYearGame${cfg.api}`](editing._id, payload);
      else await adminAPI[`createYearGame${cfg.api}`](payload);
      message.success('ذخیره شد');
      setEditing(null);
      form.resetFields();
      load();
    } catch (error) {
      message.error(error.response?.data?.message || 'خطا در ذخیره');
    }
  };

  const edit = (row) => {
    setEditing(row);
    form.setFieldsValue(denormalize(row));
  };

  const remove = async (row) => {
    try {
      await adminAPI[`deleteYearGame${cfg.api}`](row._id);
      message.success('حذف شد');
      load();
    } catch (error) {
      message.error('خطا در حذف');
    }
  };

  const columns = [
    ...cfg.fields.slice(0, 5).map(field => ({ title: field, dataIndex: field, render: value => typeof value === 'boolean' ? String(value) : Array.isArray(value) ? value.join(', ') : String(value ?? '') })),
    { title: 'عملیات', render: (_, row) => <Space><Button onClick={() => edit(row)}>ویرایش</Button><Button danger onClick={() => remove(row)}>حذف</Button></Space> }
  ];

  if (isTeacher) {
    return (
      <Card title="بررسی تسک‌های هفت‌سال هاگوارتز">
        <ReviewPanels canManageMiniGames={false} />
      </Card>
    );
  }

  return (
    <Card title="موتور هفت‌سال هاگوارتز">
      <Tabs activeKey={active} onChange={(key) => { setActive(key); setEditing(null); form.resetFields(); }} items={Object.entries(CONFIGS).map(([key, item]) => ({ key, label: item.title }))} />

      <Form form={form} layout="vertical" onFinish={submit} style={{ marginBottom: 24 }}>
        <Space wrap align="start">
          {cfg.fields.map(field => <FormField key={field} field={field} />)}
          <Form.Item label=" ">
            <Space>
              <Button type="primary" htmlType="submit">{editing ? 'ذخیره ویرایش' : 'ایجاد'}</Button>
              {editing && <Button onClick={() => { setEditing(null); form.resetFields(); }}>لغو</Button>}
            </Space>
          </Form.Item>
        </Space>
      </Form>

      <Table rowKey="_id" loading={loading} dataSource={rows} columns={columns} scroll={{ x: true }} />
      <ReviewPanels canManageMiniGames />
    </Card>
  );
}

function FormField({ field }) {
  if (['year', 'order', 'rewardKnuts', 'costKnuts', 'entryFeeKnuts', 'minPlayers', 'maxPlayers', 'maxCompletions'].includes(field)) {
    return <Form.Item name={field} label={field}><InputNumber style={{ width: 160 }} /></Form.Item>;
  }
  if (['isActive', 'isFinal', 'requiresAdminApproval'].includes(field)) {
    return <Form.Item name={field} label={field} valuePropName="checked" initialValue={true}><Switch /></Form.Item>;
  }
  if (['requiredSkillKeys', 'parentKeys'].includes(field)) {
    return <Form.Item name={field} label={field}><Select mode="tags" style={{ width: 220 }} /></Form.Item>;
  }
  if (field === 'branch') {
    return <Form.Item name={field} label={field}><Select style={{ width: 180 }} options={['charms','defense','transfiguration','potions','herbology','creatures','dark_arts','other'].map(v => ({ value: v, label: v }))} /></Form.Item>;
  }
  return <Form.Item name={field} label={field}><Input style={{ width: 220 }} /></Form.Item>;
}

function normalize(values) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined));
}

function denormalize(row) {
  return { ...row };
}

function ReviewPanels({ canManageMiniGames = true }) {
  const [taskSubmissions, setTaskSubmissions] = useState([]);
  const [registrations, setRegistrations] = useState([]);

  const loadReviews = useCallback(async () => {
    const tasks = await adminAPI.getYearGameTaskSubmissions('pending');
    setTaskSubmissions(tasks.data.submissions || []);

    if (canManageMiniGames) {
      const games = await adminAPI.getYearGameMiniGameRegistrations('pending');
      setRegistrations(games.data.registrations || []);
    } else {
      setRegistrations([]);
    }
  }, [canManageMiniGames]);

  useEffect(() => { loadReviews().catch(() => {}); }, [loadReviews]);

  const reviewTask = async (id, status) => {
    await adminAPI.reviewYearGameTaskSubmission(id, status);
    message.success('بررسی شد');
    loadReviews();
  };

  const reviewGame = async (id, status) => {
    await adminAPI.reviewYearGameMiniGameRegistration(id, status);
    message.success('بررسی شد');
    loadReviews();
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Card title="تسک‌های در انتظار تایید" style={{ marginBottom: 16 }}>
        <Table rowKey="_id" dataSource={taskSubmissions} pagination={false} columns={[
          { title: 'کاربر', render: (_, r) => r.user?.username || `${r.user?.firstName || ''} ${r.user?.lastName || ''}` },
          { title: 'تسک', render: (_, r) => r.task?.farsiTitle || r.task?.title },
          { title: 'یادداشت', dataIndex: 'note' },
          { title: 'عملیات', render: (_, r) => <Space><Button onClick={() => reviewTask(r._id, 'approved')}>تایید</Button><Button danger onClick={() => reviewTask(r._id, 'rejected')}>رد</Button></Space> }
        ]} />
      </Card>
      {canManageMiniGames && (
        <Card title="ثبت‌نام مینی‌گیم در انتظار تایید">
          <Table rowKey="_id" dataSource={registrations} pagination={false} columns={[
            { title: 'کاربر', render: (_, r) => r.user?.username || `${r.user?.firstName || ''} ${r.user?.lastName || ''}` },
            { title: 'مینی‌گیم', render: (_, r) => r.miniGame?.farsiTitle || r.miniGame?.title },
            { title: 'عملیات', render: (_, r) => <Space><Button onClick={() => reviewGame(r._id, 'approved')}>تایید</Button><Button danger onClick={() => reviewGame(r._id, 'rejected')}>رد</Button><Button onClick={() => reviewGame(r._id, 'completed')}>تکمیل/جایزه</Button></Space> }
          ]} />
        </Card>
      )}
    </div>
  );
}
